// @givmo/app-charity — signing in through the person's organization (AS-WEB30): the step every door's
// "Continue with Organization SSO" opens (`/sso`, and Board's director door's `/board/director/sso`),
// and the console's `/sso/callback`, where the employer's sign-in page sends the browser back.
//
// The step and the callback are `@givmo/passkeys`' (frames W2–W4, WF1–WF5); this file only places them
// in this console: its card or Board's director register, where signing in lands (the door's
// `redirect`, sanitized), and the way back to the door that opened the step (`back`, sanitized).
//
// A same-tab return that could not finish comes back to the step in the register it was in, carrying
// the typed address and the reason in history state (`organizationSso`), never in the address bar,
// and the step's own way back (cut 1007e's review, F5). A director's return is drawn in Board's
// register, and a director's "Lost access…" opens the way back in there too (Board's B-F3).

import { useCallback, useState } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { Card, CardBody } from '@givmo/design-system';
import { DirectorShell } from '@givmo/board';
import {
  ORGANIZATION_SSO_CALLBACK_PATH,
  ORGANIZATION_SSO_WORDS,
  OrganizationSsoCallback,
  OrganizationSsoFlow,
  keptOrganizationSsoAttemptRegister,
  organizationSsoReturnOf,
  type OrganizationSsoCallbackFailure,
  type OrganizationSsoReturnState,
} from '@givmo/passkeys';
import { useAuth } from '../../auth/auth-context';
import { SIGN_IN_LANDING_STATE } from '../../console/sidebar-frame';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { CharityDirectorHost } from '../board/director-host';
import { DIRECTOR_ROUTES } from '../board/director-routes';
import { AuthShell } from './auth-shell';
import { AUTH_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from './auth-routes';

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** A same-tab company sign-in that could not finish (AS-WEB30): what was typed, and why. */
    organizationSso?: OrganizationSsoReturnState;
  }
}

/** This console's door heading, so the step reads as that door's next screen. */
const HEADING = 'font-sans text-s22 font-w600 tracking-givmo text-text-strong';

export interface OrganizationSsoScreenProps {
  /** `director`: Board's director door opened it, so it wears Board's register (frame W2's second). */
  arrival?: 'console' | 'director';
}

export function OrganizationSsoScreen({ arrival = 'console' }: OrganizationSsoScreenProps = {}) {
  const navigate = useNavigate();
  const { signInWithOrganizationSso } = useAuth();
  // Two routes render this screen, so the search read is non-strict by necessity.
  const { redirect, back } = useSearch({ strict: false }) as { redirect?: string; back?: string };
  const here = useLocation({ select: (location) => location.href });
  // Read once by the step, as its starting address and reason.
  const carried = organizationSsoReturnOf(useLocation({ select: (location) => location.state }));
  const director = arrival === 'director';

  const target =
    sanitizeRedirectTarget(redirect) ??
    (director ? DIRECTOR_ROUTES.home : DEFAULT_AUTHENTICATED_ROUTE);
  const safeBack = sanitizeRedirectTarget(back);
  const door = director
    ? { to: DIRECTOR_ROUTES.signIn, search: redirect ? { redirect } : {} }
    : { to: safeBack ?? AUTH_ROUTES.loginHome };

  const flow = (
    <OrganizationSsoFlow
      headingClass={HEADING}
      register={arrival}
      signIn={signInWithOrganizationSso}
      onSignedIn={() => void navigate({ to: target, state: SIGN_IN_LANDING_STATE })}
      returnTo={target}
      onBackToSignIn={() => void navigate(door)}
      backTo={director ? undefined : safeBack}
      onLostAccess={() =>
        void navigate({
          to: director ? DIRECTOR_ROUTES.lostCompanyAccess : AUTH_ROUTES.lostCompanyAccess,
          search: { back: here },
        })
      }
      initialEmail={carried?.email}
      initialFailure={carried?.failure}
    />
  );

  if (director) {
    return (
      <DirectorShell
        title={ORGANIZATION_SSO_WORDS.title}
        description={ORGANIZATION_SSO_WORDS.prompt}
        testId="page-director-organization-sso"
      >
        <Card className="w-full max-w-[440px]" testId="director-organization-sso">
          <CardBody className="flex flex-col gap-20">{flow}</CardBody>
        </Card>
      </DirectorShell>
    );
  }
  return <AuthShell testId="page-organization-sso">{flow}</AuthShell>;
}

/** `/sso/callback`: the employer's sign-in page sends the browser back here (frame W4). */
export function OrganizationSsoCallbackScreen() {
  const navigate = useNavigate();
  const { signInWithOrganizationSso } = useAuth();
  const { handoff_code: handoffCode, error } = useSearch({ strict: false }) as {
    handoff_code?: string;
    error?: string;
  };

  const onSignedIn = useCallback(
    (returnTo: string) =>
      void navigate({
        to: sanitizeRedirectTarget(returnTo) ?? DEFAULT_AUTHENTICATED_ROUTE,
        replace: true,
        state: SIGN_IN_LANDING_STATE,
      }),
    [navigate],
  );
  // Which register this tab's attempt was in, read before the attempt is taken: a director's return
  // is drawn in Board's shell (Board's B-F3).
  const [register] = useState(keptOrganizationSsoAttemptRegister);
  // Back to the step, in the register it was in, with what was typed and why (WF1–WF5), and with
  // its own way back (F5).
  const onFailed = useCallback(
    ({
      register: stepRegister,
      email,
      failure,
      returnTo,
      back,
    }: OrganizationSsoCallbackFailure) => {
      const redirect = sanitizeRedirectTarget(returnTo ?? undefined);
      const stepBack =
        stepRegister === 'director' ? undefined : sanitizeRedirectTarget(back ?? undefined);
      void navigate({
        to:
          stepRegister === 'director'
            ? DIRECTOR_ROUTES.organizationSso
            : AUTH_ROUTES.organizationSso,
        search: { ...(redirect ? { redirect } : {}), ...(stepBack ? { back: stepBack } : {}) },
        replace: true,
        state: { organizationSso: { email, failure } },
      });
    },
    [navigate],
  );
  // The handoff code is one-use: out of the address bar as soon as it is read.
  const onRead = useCallback(
    () => void navigate({ to: ORGANIZATION_SSO_CALLBACK_PATH, search: {}, replace: true }),
    [navigate],
  );

  const callback = (
    <OrganizationSsoCallback
      handoffCode={handoffCode ?? null}
      error={error ?? null}
      headingClass={HEADING}
      signIn={signInWithOrganizationSso}
      onSignedIn={onSignedIn}
      onFailed={onFailed}
      onRead={onRead}
      register={register === 'director' ? 'director' : 'console'}
    />
  );
  if (register === 'director') {
    // `/sso/callback` is a root route, outside Board's family, so it brings Board's host itself.
    return (
      <CharityDirectorHost>
        <DirectorShell
          title={ORGANIZATION_SSO_WORDS.title}
          testId="page-director-organization-sso-callback"
        >
          <Card className="w-full max-w-[440px]" testId="director-organization-sso-callback">
            <CardBody className="flex flex-col gap-20">{callback}</CardBody>
          </Card>
        </DirectorShell>
      </CharityDirectorHost>
    );
  }
  return <AuthShell testId="page-organization-sso-callback">{callback}</AuthShell>;
}
